import React from 'react';
import { fetcher } from '@weezevent/weezjs-core';
import { ColorPickerLabel, Form, Button, FieldBlock, Select, TranslateDropdown } from '@weezevent/nacre';
import PropTypes from 'prop-types';
import BlockCard from '../blockCard';
import { Trans, useTranslation } from 'react-i18next';
import Styles from '../../../styles/minisite/blocks.module.css';
import EditorComponent from '../../../nacre/components/editorComponent';
import { findBlockById } from '../editForm';
import UploadImageZoneGemino from '../upload/uploadImageZoneGemino';
import IframeToURL from './iframeToURL';
import { Loader } from 'semantic-ui-react';
import ConfirmModal from '../../../components/confirmModal';
import { Validators } from '../validators';
import { LANGUAGES } from '../../../components/translations/translationModal';
import { getDefaultLanguage } from '../../organization/cross_selling/list/translationUtils';

const TRANSLATED_FIELDS = ['title', 'text'];

const FilesDownload = ({ organization, state, handleChange }) => {
    const { t } = useTranslation();
    const files = state.payload.files || [];
    const onUpload = React.useCallback(
        ({ name, url }) => {
            const newFilesList = [...files];
            const objToUpdateIndex = newFilesList.findIndex(file => file.url === url);
            // We find an object that already exist with this url so we just have to update his name
            if (objToUpdateIndex !== -1) {
                newFilesList[objToUpdateIndex].name = name;
            } else {
                newFilesList.push({ name: name || url, url: url });
            }
            handleChange(null, { name: 'files', value: newFilesList });
        },
        [files, handleChange]
    );
    const onDelete = React.useCallback(
        (value, index) => {
            const newFilesList = [...files];
            newFilesList.splice(index, 1);
            handleChange(null, { name: 'files', value: newFilesList });
        },
        [files, handleChange]
    );

    return (
        <UploadImageZoneGemino
            inline
            inModal
            organization={organization}
            onUpload={onUpload}
            icon={'file'}
            files={files}
            onDelete={onDelete}
            fileType="file"
            textInfo={t('ticket.event.minisite.colors_options.dropzone_files')}
        />
    );
};

const ImagesCarousel = ({ organization, state, handleChange }) => {
    const { t } = useTranslation();
    const images = state.payload.images || [];
    const onUpload = React.useCallback(
        value => {
            const newImagesList = [...images];
            newImagesList.push({ url: value });
            handleChange(null, { name: 'images', value: newImagesList });
        },
        [images, handleChange]
    );
    const onDelete = React.useCallback(
        (value, index) => {
            const newImagesList = [...images];
            newImagesList.splice(index, 1);
            handleChange(null, { name: 'images', value: newImagesList });
        },
        [images, handleChange]
    );

    return (
        <UploadImageZoneGemino
            inline
            inModal
            mode="carousel"
            organization={organization}
            onUpload={url => onUpload(url)}
            icon={'picture'}
            images={images.map(({ url }) => url)}
            onDelete={onDelete}
            minItems={2}
            textInfo={t('ticket.event.minisite.colors_options.dropzone_carousel')}
            maxSize={5242880}
        />
    );
};

const Images = ({ organization, dispatch, handleSubmit, state, onCloseModal }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = React.useState(true);

    const onUpload = React.useCallback(
        value => {
            onCloseModal();
            handleSubmit({ type: state.selectedType, url: value });
            setIsOpen(false);
        },
        [handleSubmit]
    );

    const closeModal = React.useCallback(() => {
        if (state.initialBlock) {
            onCloseModal();
        }
        setIsOpen(false);
        dispatch({ type: 'resetType' });
    }, [state]);

    return (
        <UploadImageZoneGemino
            openModal={isOpen}
            organization={organization}
            onUpload={url => onUpload(url)}
            icon={'picture'}
            onCancel={closeModal}
            textInfo={t('ticket.event.minisite.colors_options.dropzone_image')}
            maxSize={5242880}
        />
    );
};

const getComponent = format => {
    switch (format) {
        case 'color':
            return ColorPickerLabel;
        default:
            return Form.Input;
    }
};

const prepareState = (blocksPatern, type, hasTranslationCapability) => {
    const schema = blocksPatern.find(block => block.name === type).schema;
    return {
        selectedType: type,
        schema: schema,
        inputs:
            'properties' in schema
                ? Object.entries(schema.properties).map(([key, value]) => {
                      return {
                          Component: getComponent(value.format),
                          name: key,
                          label: `ticket.event.minisite.block.type.${key}.label`,
                          placeholder: `ticket.event.minisite.block.type.${key}.placeholder`,
                          type: value.type,
                          format: !TRANSLATED_FIELDS.includes(key) ? value.format : value.additionalProperties?.format,
                          enum: 'enum' in value ? value.enum : null,
                          default: 'default' in value ? value.default : null
                      };
                  })
                : []
    };
};

const getInitialValues = state => {
    let values = {};
    const block = findBlockById(state.blocks, state.initialBlock.id);
    if (block) {
        values['name'] = block.name;
        Object.entries(block.payload).map(([key, value]) => {
            values[key] = value;
            return null;
        });
    }
    return { payload: values };
};

const reduce = (state, action) => {
    switch (action.type) {
        case 'blockPatterns':
            return {
                ...state,
                blockPatterns: action.value,
                ...(state.initialBlock && getInitialValues(state)),
                ...(state.initialBlock && prepareState(action.value, state.initialBlock.type, action.hasTranslationCapability))
            };
        case 'inputs':
            return { ...state, inputs: action.value };
        case 'formError':
            return { ...state, formError: action.value };
        case 'payload':
            return {
                ...state,
                payload: {
                    ...state.payload,
                    [action.name]: TRANSLATED_FIELDS.includes(action.name)
                        ? {
                              ...state.payload[action.name],
                              ...action.value
                          }
                        : action.value
                }
            };
        case 'resetType':
            return {
                ...state,
                inputs: []
            };
        case 'selectedType':
            return {
                ...state,
                payload: {},
                ...prepareState(state.blockPatterns, action.value, action.hasTranslationCapability)
            };
        default:
            return { ...state };
    }
};

const isDisabled = state => {
    // if selected block is follow, at least one input have to be fullfiled
    if (state.selectedType === 'follow' && Object.values(state.payload).every(x => x === '')) {
        return true;
    }
    // if selected block is image, at least one image have to be selected
    if (state.selectedType === 'image' && (Object.values(state.payload).length < 1 || state.payload.url === null)) {
        return true;
    }
    // if selected block is video, full url
    if (state.selectedType === 'video' && (Object.values(state.payload).length < 1 || state.payload.src === null || state.payload.src === '')) {
        return true;
    }
    // if selected block is carousel, at least two images have to be selected
    if (state.selectedType === 'carousel' && (Object.keys(state.payload).length < 1 || state.payload.images.length < 2)) {
        return true;
    }
};

export default function BlockForm({ onSubmit, blocks, initialBlock, place, additionnalData, organization, onCloseModal, siteLanguage, eventLanguage, hasTranslationCapability }) {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(true);

    const [isLoading, setIsLoading] = React.useState(false);
    const [openModal, setOpenModal] = React.useState(false);
    const [state, dispatch] = React.useReducer(reduce, initialBlock, initialBlock => {
        return {
            blockPatterns: [],
            inputs: [],
            payload: {},
            blocks: blocks,
            selectedType: initialBlock && initialBlock.type,
            initialBlock: initialBlock,
            formError: []
        };
    });

    // Determine the default language with fallbacks if siteLanguage or eventLanguage is not available
    const defaultLanguage = siteLanguage || eventLanguage || getDefaultLanguage(organization, organization.languages);

    const [languageSelection, setLanguageSelection] = React.useState({
        current: defaultLanguage,
        selected: [defaultLanguage]
    });

    React.useEffect(() => {
        fetcher.get('/sites/blocks-patterns', { cache: false }).then(({ response }) => {
            setLoading(false);
            return dispatch({ type: 'blockPatterns', value: response, hasTranslationCapability });
        });
    }, [hasTranslationCapability]);

    const handleSubmit = React.useCallback(() => {
        let errors = {};

        // First check all required fields that are not fill
        const requiredFields = state.schema.required ? state.schema.required.filter(input => !(input in state.payload) || state.payload[input] === '') : [];
        // add to errors object required field
        for (let field of requiredFields) {
            errors[field] = t('ticket.common.required');
        }

        // After check if there's some value that didn't respect there format
        const invalidFieldFormat = Object.keys(state.schema.properties).reduce((aggregator, key) => {
            const ret =
                'format' in state.schema.properties[key] &&
                state.payload[key] &&
                Validators[state.schema.properties[key].format] &&
                !Validators[state.schema.properties[key].format](state.payload[key]);

            if (ret) {
                aggregator.push([key, state.schema.properties[key].format]);
                return aggregator;
            }
            return aggregator;
        }, []);

        // and add them to errors object
        invalidFieldFormat.forEach(field => field && (errors[field[0]] = t('ticket.event.minisite.format-error', { format: field[1] })));

        dispatch({ type: 'formError', value: errors });
        if (!errors || Object.keys(errors).length === 0) {
            setIsLoading(true);
            onSubmit({ type: state.selectedType, ...state.payload });
        }
    }, [state]);

    const languageList = React.useMemo(
        () =>
            organization.languages.map(lang => ({
                label: LANGUAGES[lang.toLowerCase()],
                value: lang
            })),
        [organization]
    );

    const getLanguageKey = (currentLang, defaultLang) => {
        return currentLang === defaultLang ? 'default' : currentLang;
    };

    const getInputValue = (payload, name, currentLang, defaultLang) => {
        if (!TRANSLATED_FIELDS.includes(name)) {
            return payload[name] || '';
        }
        const languageKey = getLanguageKey(currentLang, defaultLang);
        return payload[name]?.[languageKey] || '';
    };

    const handleChange = React.useCallback(
        (ev, data) => {
            data = data || ev.target;
            const { name, value } = data;

            // If the field is not a translated field, we can directly update the payload
            if (!TRANSLATED_FIELDS.includes(name)) {
                return dispatch({ type: 'payload', name: data.name, value: data.value });
            }

            // If the field is a translated field, we need to update the payload with the current language key
            const languageKey = getLanguageKey(languageSelection.current, defaultLanguage);
            dispatch({
                type: 'payload',
                name: name,
                value: {
                    [languageKey]: value
                }
            });
        },
        [languageSelection, defaultLanguage]
    );

    const handleChangeLang = React.useCallback(value => {
        setLanguageSelection(prevState => ({
            current: value,
            selected: prevState.selected.includes(value) ? prevState.selected : [...prevState.selected, value]
        }));
    }, []);

    const handleClick = React.useCallback(
        value => {
            if (!value) {
                dispatch({ type: 'resetType' });
            } else {
                dispatch({
                    type: 'selectedType',
                    value: value,
                    hasTranslationCapability
                });
            }
        },
        [hasTranslationCapability]
    );

    const allowPlacement = name => {
        const schema = state.blockPatterns.find(schema => schema.name === name);
        return schema ? Boolean(schema.tags.includes(place)) : false;
    };

    const getTranslation = value => {
        if (['h2', 'h3', 'h4'].includes(value)) {
            return t(`ticket.event.minisite.block.${value}`);
        } else {
            return value;
        }
    };

    if (loading) {
        return (
            <div style={{ height: '400px' }} className="loading">
                <Loader active />
            </div>
        );
    }

    const payloadIsEmpty = Object.values(state.payload).every(x => x === null || x === '' || !x?.length);

    const confirmReturn = () => {
        setOpenModal(false);
        dispatch({ type: 'resetType' });
    };

    return (
        <>
            <ConfirmModal
                onClose={() => setOpenModal(false)}
                onAccept={confirmReturn}
                objectToSave={openModal && !payloadIsEmpty}
                labelBody={t('ticket.event.minisite.block.confirm-return')}
            />
            <Form>
                {state.initialBlock || state.inputs.length ? (
                    <>
                        <div className={Styles['modal-title']}>
                            <Trans i18nKey={`ticket.event.minisite.block.title-${state.selectedType}`} />
                        </div>
                        <div className={Styles['modal-content']}>
                            {state.inputs.some(({ name }) => TRANSLATED_FIELDS.includes(name)) && hasTranslationCapability && (
                                <div className={Styles['language-select']}>
                                    <Form.Field>
                                        <TranslateDropdown
                                            defaultValue={defaultLanguage}
                                            defaultText={t('ticket.event.minisite.translations.default')}
                                            languages={languageList}
                                            onChange={handleChangeLang}
                                            selected={languageSelection.selected}
                                        />
                                    </Form.Field>
                                </div>
                            )}
                            {state.inputs.map(({ Component, label, placeholder, ...props }) => {
                                const isWidget = initialBlock?.type === 'event-widget';
                                switch (props.format) {
                                    case 'textarea':
                                        return (
                                            <EditorComponent
                                                block_formats={`${t('ticket.event.minisite.paragraph')}=p; ${t('ticket.event.minisite.title')}=h2; ${t(
                                                    'ticket.event.minisite.title'
                                                )}=h3; ${t('ticket.event.minisite.title')}=h4`}
                                                key={props.name}
                                                menubar={false}
                                                description={getInputValue(state.payload, props.name, languageSelection.current, defaultLanguage)}
                                                onChange={(content, editor) => handleChange(null, { name: props.name, value: content })}
                                                placeholder={t('ticket.creationEvent.infos.formPlaceholderDescription')}
                                                fontFamily={additionnalData && additionnalData.font_family}
                                                errors={state.formError && props.name in state.formError ? [state.formError[props.name]] : null}
                                            />
                                        );
                                    case 'image':
                                        return (
                                            <Form.Field>
                                                <Form.Label>{t('ticket.event.minisite.block.choice-image')}</Form.Label>
                                                <Images organization={organization} state={state} handleSubmit={onSubmit} onCloseModal={onCloseModal} dispatch={dispatch} />
                                            </Form.Field>
                                        );
                                    case 'arrayImage':
                                        return (
                                            <Form.Field>
                                                <Form.Label>{t('ticket.event.minisite.block.choice-image')}</Form.Label>
                                                <ImagesCarousel organization={organization} state={state} handleChange={handleChange} />
                                            </Form.Field>
                                        );
                                    case 'arrayFiles':
                                        return (
                                            <Form.Field>
                                                <Form.Label>{t('ticket.event.minisite.block.choice-file')}</Form.Label>
                                                <FilesDownload organization={organization} state={state} handleChange={handleChange} name={props.name} />
                                            </Form.Field>
                                        );
                                    case 'iframeToURL':
                                        return <IframeToURL value={state.payload['src']} onChange={value => handleChange(null, { name: 'src', value: value })} />;
                                    case 'select':
                                        return (
                                            <Form.Field>
                                                <Form.Label>{t('ticket.event.minisite.block.choice-tag-format')}</Form.Label>
                                                <Select
                                                    onChange={value => handleChange(null, { name: 'format', value: value })}
                                                    options={props.enum.map(value => ({ text: getTranslation(value), value: value }))}
                                                    value={state.payload[props.name] || props.default}
                                                />
                                            </Form.Field>
                                        );
                                    case 'color':
                                        return null;
                                    default:
                                        return (
                                            <FieldBlock
                                                key={props.name}
                                                value={getInputValue(state.payload, props.name, languageSelection.current, defaultLanguage)}
                                                name={props.name}
                                                as={Form.Input}
                                                label={
                                                    isWidget ? t([`ticket.event.minisite.block.type.widget.label`]) : t([`ticket.event.minisite.block.type.${props.name}.label`])
                                                }
                                                placeholder={
                                                    isWidget
                                                        ? t([`ticket.event.minisite.block.type.widget.placeholder`])
                                                        : t([`ticket.event.minisite.block.type.${props.name}.placeholder`])
                                                }
                                                onChange={event => handleChange(null, { name: props.name, value: event.target.value, format: props.format })}
                                                errors={state.formError && props.name in state.formError ? [state.formError[props.name]] : null}
                                            />
                                        );
                                }
                            })}
                        </div>
                        <div className={Styles['form-modal-buttons']} style={{ justifyContent: 'flex-end' }}>
                            {!state.initialBlock && (
                                <Button primary inverted isLoading={isLoading} onClick={() => (payloadIsEmpty ? dispatch({ type: 'resetType' }) : setOpenModal(true))}>
                                    <Trans i18nKey="ticket.common.action.back" />
                                </Button>
                            )}
                            <Button primary isLoading={isLoading} onClick={handleSubmit} disabled={isDisabled(state)}>
                                {state.initialBlock ? <Trans i18nKey="ticket.common.action.save" /> : <Trans i18nKey="ticket.event.minisite.block.submit" />}
                            </Button>
                        </div>
                    </>
                ) : (
                    <div>
                        <h5>{t([`ticket.event.minisite.block.title.title`])}</h5>
                        <div className={Styles['card-block-container']}>
                            <BlockCard mini title={t([`ticket.event.minisite.block.name.text`])} icon="paragraph" onClick={() => handleClick('text')} />
                            <BlockCard mini title={t([`ticket.event.minisite.block.name.title`])} icon="text" onClick={() => handleClick('card-title')} />
                            <BlockCard mini title={t([`ticket.event.minisite.block.name.image`])} icon="image" onClick={() => handleClick('image')} />
                            {allowPlacement('carousel') && (
                                <BlockCard mini title={t([`ticket.event.minisite.block.name.gallery`])} icon="diaporama" onClick={() => handleClick('carousel')} />
                            )}
                            {allowPlacement('video') && <BlockCard mini title={t([`ticket.event.minisite.block.name.video`])} icon="videos" onClick={() => handleClick('video')} />}
                        </div>
                        <h5>{t([`ticket.event.minisite.block.title.title-advanced`])}</h5>
                        <div className={`${Styles['card-block-container']} ${Styles['card-block-split-2']}`}>
                            {allowPlacement('facebook') && (
                                <BlockCard
                                    title={t([`ticket.event.minisite.block.name.facebook`])}
                                    icon="facebook"
                                    onClick={() => onSubmit({ type: 'facebook', ...state.payload })}
                                >
                                    {t([`ticket.event.minisite.block.type.facebook.description`])}
                                </BlockCard>
                            )}
                            <BlockCard title={t([`ticket.event.minisite.block.name.file`])} icon="folder-open" onClick={() => handleClick('files-download')}>
                                {t([`ticket.event.minisite.block.type.file.description`])}
                            </BlockCard>
                            {allowPlacement('follow') && (
                                <BlockCard title={t([`ticket.event.minisite.block.type.follow.name`])} icon="network" onClick={() => handleClick('follow')}>
                                    {t([`ticket.event.minisite.block.type.follow.description`])}
                                </BlockCard>
                            )}
                            <BlockCard title={t([`ticket.common.action.share`])} icon="share" onClick={() => onSubmit({ type: 'socials-share', ...state.payload })}>
                                {t([`ticket.event.minisite.block.type.share.description`])}
                            </BlockCard>
                            {/*<BlockCard
                                disabled
                                title={t([`ticket.event.minisite.block.name.counter`])}
                                icon="timer"
                                statusProps={{
                                    text: t([`ticket.event.minisite.block.name.soon`]),
                                    color: 'orange'
                                }}
                            >
                                {t([`ticket.event.minisite.block.type.counter.description`])}
                            </BlockCard>*/}
                            {
                                // Trick to keep always a beautiful grid
                                // 2 blocks and 2 conditionnals block
                                // If only 3 blocks showed, we create an empty block
                                <div style={{ flex: '50% 1' }} />
                            }
                        </div>
                    </div>
                )}
            </Form>
        </>
    );
}

BlockForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    organization: PropTypes.object.isRequired,
    onCloseModal: PropTypes.func,
    hasTranslationCapability: PropTypes.bool
};
